// React and related imports
import React, { useEffect } from 'react';

// i18 Translation
import { TFunction } from 'i18next';

// Types and Interfaces
import { ContextState } from '../../hooks/useContentContextState';
import { IMultiLanguageValue } from '../../common/interfaces/MultiLanguageValue.interface';

// Custom Components
import { MultiLanguageFieldGroup } from '../Shared/MultiLanguageInput';

interface MultiLangContextInputsProps {
  t: TFunction<'translation', undefined>;
  contextState: ContextState;
  updateField: (
    field: keyof ContextState,
    value: IMultiLanguageValue[]
  ) => void;
  validateField: (field: string, value: string) => void;
}

export const MultiLangContextInputs: React.FC<MultiLangContextInputsProps> = ({
  t,
  contextState,
  updateField,
  validateField,
}) => {
  // Validation effects
  useEffect(() => {
    const titles = contextState?.title;
    const hasValidTitle = titles?.some((t) => t.value.trim() !== '');
    const longTitle = titles?.find((t) => t.value.trim().length > 50)?.value;

    if (titles?.length === 0 || (hasValidTitle && !longTitle)) {
      validateField('title', hasValidTitle ? 'valid' : '');
    } else if (longTitle) {
      validateField('title', longTitle);
    }
  }, [contextState?.title]);

  useEffect(() => {
    const descriptions = contextState?.description;
    const hasValidDesc = descriptions?.some((d) => d.value.trim() !== '');
    const longDesc = descriptions?.find(
      (d) => d.value.trim().length > 300
    )?.value;

    if (descriptions?.length === 0 || (hasValidDesc && !longDesc)) {
      validateField('description', hasValidDesc ? 'valid' : '');
    } else if (longDesc) {
      validateField('description', longDesc);
    }
  }, [contextState?.description]);

  return (
    <MultiLanguageFieldGroup
      fields={[
        {
          id: 'title-input',
          label: 'contentContext.title',
          type: 'text',
          placeholder: 'placeholders.title',
          required: true,
          autoFocus: true,
          validation: {
            maxLength: 50,
          },
        },
        {
          id: 'description-input',
          label: 'contentContext.description',
          type: 'textarea',
          placeholder: 'placeholders.description',
          required: true,
          rows: 3,
          className: 'mt-1',
          validation: {
            maxLength: 300,
          },
        },
      ]}
      values={{
        'title-input': contextState?.title || [],
        'description-input': contextState?.description || [],
      }}
      onChange={(fieldId, value) => {
        if (fieldId === 'title-input') {
          updateField('title' as keyof ContextState, value);
        } else if (fieldId === 'description-input') {
          updateField('description' as keyof ContextState, value);
        }
      }}
      sharedLanguageSelector={true}
      cardWrapper={true}
      t={t}
    />
  );
};
